/* eslint-disable react-refresh/only-export-components */
/* eslint-disable @typescript-eslint/no-explicit-any */
import type { Notification } from "@/services/app/notification";
import { NotificationService } from "@/services/app/notification";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext } from "react";

interface NotificationContextType {
  notifications: Notification[] | undefined;
  isLoading: boolean;
  markAsRead: (id: string) => Promise<void>;
  refetch: () => Promise<any>;
}

const NotificationContext = createContext<NotificationContextType>({} as NotificationContextType);

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const queryClient = useQueryClient();
  const notificationService = new NotificationService();

  const {
    data: notifications,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => notificationService.getNotifications(),
  });

  const markAsReadMutation = useMutation({
    mutationFn: (id: string) => notificationService.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const markAsRead = useCallback(
    async (id: string) => {
      await markAsReadMutation.mutateAsync(id);
    },
    [markAsReadMutation]
  );

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        isLoading,
        markAsRead,
        refetch,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotification = () => useContext(NotificationContext); 